import { Form as AntdForm, DatePicker, Input, Select } from 'antd'
import { NAMESPACE } from 'locale'
import { incidentReportsConstants } from 'locale/incidentReports'
import { useTranslation } from 'react-i18next'
import StepCheckbox from '../../StepCheckbox'
import StepField from '../../StepField'
import { StepReveal, StepRevealGroup } from '../../StepReveal'
import { DATE_FORMAT } from '../../stepLayout'
import { useTeamMemberOptions } from '../../stepOptions'
import { isBlank } from '../../stepValues'

export type DebriefKind = 'hotDebrief' | 'coldDebrief'

const DETAIL_FIELDS = [
  'conductedAt',
  'debriefedUsers',
  'notes',
  'preventativeActions'
] as const

type DebriefFieldsProps = {
  kind: DebriefKind
  testId: string
}

const LABEL_KEYS: Record<DebriefKind, string> = {
  coldDebrief: incidentReportsConstants.CASE_V2_INVESTIGATION_COLD_DEBRIEF,
  hotDebrief: incidentReportsConstants.CASE_V2_INVESTIGATION_HOT_DEBRIEF
}

/**
 * One debrief: its checkbox, and once ticked, what was learned, who attended
 * and when it was completed. The cold debrief also records improvement actions.
 */
const DebriefFields = ({ kind, testId }: DebriefFieldsProps) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const form = AntdForm.useFormInstance()
  const isRecorded = AntdForm.useWatch([kind, 'isRecorded'], form)
  const attendeeOptions = useTeamMemberOptions({
    saved: AntdForm.useWatch([kind, 'debriefedUsers'], form)
  })
  const isCold = kind === 'coldDebrief'
  const fieldTestId = `${testId}-${isCold ? 'cold' : 'hot'}-debrief`

  return (
    <StepRevealGroup>
      <AntdForm.Item
        name={[kind, 'isRecorded']}
        valuePropName="checked"
        noStyle
      >
        <StepCheckbox
          confirmUntick={{
            body: t(
              incidentReportsConstants.CASE_V2_INVESTIGATION_DEBRIEF_UNTICK_CONFIRM_BODY
            ),
            isNeeded: (form) =>
              DETAIL_FIELDS.some(
                (field) => !isBlank(form.getFieldValue([kind, field]))
              ),
            onConfirm: (form) =>
              form.setFieldValue(kind, {
                conductedAt: null,
                debriefedUsers: [],
                isRecorded: false,
                notes: '',
                preventativeActions: ''
              }),
            title: t(
              incidentReportsConstants.CASE_V2_INVESTIGATION_DEBRIEF_UNTICK_CONFIRM_TITLE
            )
          }}
          testId={fieldTestId}
        >
          {t(LABEL_KEYS[kind])}
        </StepCheckbox>
      </AntdForm.Item>
      {isRecorded && (
        <StepReveal data-testid={`${fieldTestId}-fields`}>
          <StepField
            name={[kind, 'notes']}
            label={t(
              isCold
                ? incidentReportsConstants.CASE_V2_INVESTIGATION_LEARNING_IDENTIFIED
                : incidentReportsConstants.CASE_V2_INVESTIGATION_IMMEDIATE_LEARNING
            )}
            hint={
              isCold
                ? undefined
                : t(
                    incidentReportsConstants.CASE_V2_INVESTIGATION_IMMEDIATE_LEARNING_HINT
                  )
            }
          >
            <Input.TextArea
              data-testid={`${fieldTestId}-notes`}
              autoSize={{ minRows: 3 }}
            />
          </StepField>
          {isCold && (
            <StepField
              name={[kind, 'preventativeActions']}
              label={t(
                incidentReportsConstants.CASE_V2_INVESTIGATION_IMPROVEMENT_ACTIONS
              )}
            >
              <Input.TextArea
                data-testid={`${fieldTestId}-improvement-actions`}
                autoSize={{ minRows: 3 }}
              />
            </StepField>
          )}
          <StepField
            name={[kind, 'debriefedUsers']}
            label={t(incidentReportsConstants.CASE_V2_INVESTIGATION_ATTENDEES)}
          >
            <Select
              data-testid={`${fieldTestId}-attendees`}
              mode="multiple"
              allowClear
              options={attendeeOptions}
              optionFilterProp="label"
              placeholder={t(
                incidentReportsConstants.CASE_V2_SELECT_PLACEHOLDER_MULTI
              )}
            />
          </StepField>
          <StepField
            name={[kind, 'conductedAt']}
            label={t(
              incidentReportsConstants.CASE_V2_INVESTIGATION_COMPLETED_DATE
            )}
          >
            <DatePicker
              data-testid={`${fieldTestId}-completed-date`}
              format={DATE_FORMAT}
            />
          </StepField>
        </StepReveal>
      )}
    </StepRevealGroup>
  )
}

export default DebriefFields
